import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container,
  Paper,
  Grid,
  useTheme,
  alpha
} from '@mui/material';
import { 
  useNavigate, 
  useLocation 
} from 'react-router-dom';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, isAuthenticated } = useAuth();

  const handleGoHome = () => {
    if (isAuthenticated && user) {
      // Redirecionar baseado no role do usuário - simplificado sem usePermissions
      if (user.role === 'SUPER_ADMIN') {
        window.location.href = '/dashboard';
      } else {
        navigate('/dashboard');
      }
    } else {
      navigate('/');
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      handleGoHome();
    }
  };

  const getSuggestions = () => {
    const suggestions = [];
    
    if (isAuthenticated && user) {
      if (user.role === 'SUPER_ADMIN') {
        suggestions.push(
          { label: 'Dashboard Admin', path: '/admin-dashboard', icon: <HomeIcon /> },
          { label: 'Gerenciar Usuários', path: '/admin/users', icon: <SearchIcon /> },
          { label: 'Configurações', path: '/admin/settings', icon: <HelpIcon /> }
        );
      } else if (user.role === 'ASSOCIACAO') {
        suggestions.push(
          { label: 'Dashboard', path: '/dashboard', icon: <HomeIcon /> },
          { label: 'Campanhas', path: '/campaigns', icon: <SearchIcon /> },
          { label: 'Sorteios', path: '/draws', icon: <HelpIcon /> }
        );
      } else {
        suggestions.push(
          { label: 'Sorteios Disponíveis', path: '/live-draw-selector', icon: <HomeIcon /> },
          { label: 'Meus Cupons', path: '/my-coupons', icon: <SearchIcon /> }
        );
      }
    } else {
      suggestions.push(
        { label: 'Fazer Login', path: '/login', icon: <HomeIcon /> },
        { label: 'Criar Conta', path: '/register', icon: <SearchIcon /> }
      );
    }
    
    return suggestions;
  };

  return (
    <Container maxWidth="md">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        textAlign="center"
        py={4}
      >
        <Paper
          elevation={3}
          sx={{
            p: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            width: '100%',
            maxWidth: 600
          }}
        >
          {/* Número 404 animado */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '6rem', md: '8rem' },
                fontWeight: 'bold',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 4px 8px rgba(0,0,0,0.1)',
                animation: 'pulse 2s infinite'
              }}
            >
              404
            </Typography>
          </Box>

          {/* Mensagem principal */}
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 2
            }}
          >
            Página não encontrada
          </Typography>

          <Typography 
            variant="body1" 
            color="text.secondary" 
            paragraph
            sx={{ fontSize: '1.1rem', mb: 4 }}
          >
            A página que você está procurando não existe ou foi movida.
            <br />
            <strong>URL tentada:</strong> {location.pathname}
          </Typography>

          {/* Botões de ação */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                startIcon={<HomeIcon />}
                onClick={handleGoHome}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: theme.shadows[4]
                }}
              >
                Ir para o Início
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                size="large"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Voltar
              </Button>
            </Grid>
          </Grid>

          {/* Sugestões de navegação */}
          <Box sx={{ mt: 4 }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 2
              }}
            >
              Ou tente uma dessas opções:
            </Typography>
            
            <Grid container spacing={1}>
              {getSuggestions().map((suggestion, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Button
                    variant="text"
                    fullWidth
                    startIcon={suggestion.icon}
                    onClick={() => navigate(suggestion.path)}
                    sx={{
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    {suggestion.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Informações do usuário (se logado) */}
          {isAuthenticated && user && (
            <Box 
              sx={{ 
                mt: 4, 
                pt: 3, 
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}` 
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Logado como: <strong>{user.name}</strong> ({user.role})
              </Typography>
            </Box>
          )}
        </Paper>

        {/* CSS para animação */}
        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); }
              50% { transform: scale(1.05); }
              100% { transform: scale(1); }
            }
          `}
        </style>
      </Box>
    </Container>
  );
};

export default NotFound; 